import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users2, Lock, HeartHandshake, ChevronRight } from "lucide-react";
import { Header } from "@/components/shared";
import { listCommunities } from "@/lib/api";
import { CommunityIcon, accentClasses } from "@/lib/communityIcons";
import { Skeleton } from "@/components/ui/skeleton";

export default function Communities() {
  const [items, setItems] = useState(null);
  useEffect(() => { listCommunities().then(setItems); }, []);

  return (
    <div className="min-h-screen grain">
      <Header title="Communities" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 text-primary">
          <HeartHandshake className="w-5 h-5" />
          <span className="text-xs font-bold tracking-[0.2em] uppercase">Inclusive spaces</span>
        </div>
        <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Communities on SkillBridge</h1>
        <p className="mt-2 text-foreground/80 max-w-2xl">
          Safe, opt-in spaces where workers self-identify and employers publicly pledge inclusive hiring.
          <span className="inline-flex items-center gap-1 ml-2 text-xs text-muted-foreground"><Lock className="w-3 h-3" /> Everything is private by default — you choose what shows on your resume.</span>
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {items === null
            ? [0,1,2,3,4,5].map((i) => <Skeleton key={i} className="h-40 rounded-lg" />)
            : items.map((c, i) => <CommunityCard key={c.slug} c={c} index={i} />)}
        </div>

        <div className="mt-10 rounded-lg border border-dashed border-border p-5 text-sm text-muted-foreground">
          <p><b className="text-foreground">Our promise.</b> Joining a community never requires a diagnosis or proof — it's self-identification. Employer pledges are public commitments, not certifications. Anyone can leave silently at any time.</p>
        </div>
      </div>
    </div>
  );
}

const CommunityCard = ({ c, index }) => {
  const ac = accentClasses(c.accent);
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
      <Link to={`/communities/${c.slug}`} data-testid={`community-card-${c.slug}`}
            className={`block rounded-lg border border-border bg-card p-5 hover:ring-2 ${ac.ring} transition-shadow`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl ${ac.bg} ${ac.text} flex items-center justify-center shrink-0`}>
            <CommunityIcon name={c.icon} className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-lg font-bold">{c.name}</div>
            <p className="text-sm text-muted-foreground mt-0.5">{c.short_desc}</p>
            <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Users2 className="w-3.5 h-3.5" />{c.member_count} members</span>
              <span className="inline-flex items-center gap-1"><HeartHandshake className="w-3.5 h-3.5" />{c.pledged_employer_count} pledged employers</span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground mt-1 shrink-0" />
        </div>
      </Link>
    </motion.div>
  );
};
